import React from 'react';
import {
  Box,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Tooltip,
  List,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
} from '@mui/icons-material';
import { useTranslation } from '../../../../../i18n';
import { BRAND_COLORS, alpha } from '../../../../../styles/theme';
import { InventoryLocation } from '../../../../../types/api';

interface LocationTreeNodeProps {
  location: InventoryLocation;
  locations: InventoryLocation[];
  selectedId: string | null;
  expandedIds: Set<string>;
  onSelect: (id: string) => void;
  onToggle: (id: string) => void;
  onEdit: (location: InventoryLocation) => void;
  onDelete: (location: InventoryLocation) => void;
  depth: number;
  t: ReturnType<typeof useTranslation>['t'];
  filteredIds?: Set<string> | null;
}

export const LocationTreeNode: React.FC<LocationTreeNodeProps> = ({
  location,
  locations,
  selectedId,
  expandedIds,
  onSelect,
  onToggle,
  onEdit,
  onDelete,
  depth,
  t,
  filteredIds,
}) => {
  const children = locations.filter(
    (l) => l.parent_id === location.id && (!filteredIds || filteredIds.has(l.id))
  );
  const hasChildren = children.length > 0;
  const isExpanded = expandedIds.has(location.id);
  const isSelected = selectedId === location.id;

  return (
    <>
      <ListItemButton
        selected={isSelected}
        onClick={() => onSelect(location.id)}
        sx={{
          pl: 2 + depth * 2,
          py: 0.5,
          minHeight: 36,
          '&.Mui-selected': {
            backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.08),
            '&:hover': { backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.12) },
          },
        }}
      >
        {hasChildren ? (
          <ListItemIcon
            sx={{ minWidth: 28, cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(location.id);
            }}
          >
            {isExpanded ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
          </ListItemIcon>
        ) : (
          <ListItemIcon sx={{ minWidth: 28 }}>
            <Box sx={{ width: 20 }} />
          </ListItemIcon>
        )}
        <ListItemIcon sx={{ minWidth: 28, color: BRAND_COLORS.forestGreen.main }}>
          {isExpanded && hasChildren ? (
            <FolderOpenIcon fontSize="small" />
          ) : (
            <FolderIcon fontSize="small" />
          )}
        </ListItemIcon>
        <ListItemText
          primary={location.name}
          secondary={t(
            `inventory.locationTypes.${location.location_type}` as Parameters<typeof t>[0]
          )}
          primaryTypographyProps={{ variant: 'body2', noWrap: true }}
          secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
        />
        <Box sx={{ display: 'flex', opacity: 0.6, '&:hover': { opacity: 1 } }}>
          <Tooltip title={t('inventory.editLocation')}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(location);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('inventory.deleteLocation')}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(location);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </ListItemButton>
      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {children
              .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name))
              .map((child) => (
                <LocationTreeNode
                  key={child.id}
                  location={child}
                  locations={locations}
                  selectedId={selectedId}
                  expandedIds={expandedIds}
                  onSelect={onSelect}
                  onToggle={onToggle}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  depth={depth + 1}
                  t={t}
                  filteredIds={filteredIds}
                />
              ))}
          </List>
        </Collapse>
      )}
    </>
  );
};
